import { Box, Card, CardContent, styled } from '@mui/material';
import { Handle, Node } from '@xyflow/react';
import { NodeProps, Position } from '@xyflow/system';
import { memo, useEffect, useRef } from 'react';
import { HandleId } from './WorkflowStepNode';

export const SmallStepNodeWidth = 100;

const PREFIX = 'WorkflowSmallStepNode';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  cardShadowSelected: `${PREFIX}-cardShadowSelected`,
  header: `${PREFIX}-header`,
  handle: `${PREFIX}-handle`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.card}`]: {
    width: SmallStepNodeWidth,
    margin: 5,
  },
  [`& .${classes.cardContent}`]: {
    padding: 0,
  },
  [`& .${classes.cardShadowSelected}`]: {
    boxShadow: `0px 0px 10px ${theme.palette.primary.main}`,
  },
  [`& .${classes.header}`]: {
    textAlign: 'center',
    marginTop: 0,
    marginBottom: 0,
  },
  [`& .${classes.handle}`]: {
    opacity: 0,
  },
}));

export type WorkflowSmallStepNodeData = {
  row: number;
  title: string;
  actions: string[];
  onHeightChange?: (id: string, height: number) => void;
};

type WorkflowSmallStepNodeType = Node<WorkflowSmallStepNodeData, 'stepData'>;

const WorkflowSmallStepNode = ({
  id,
  data,
  isConnectable,
  selected,
}: NodeProps<WorkflowSmallStepNodeType>) => {
  const { title, actions } = data;
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (nodeRef.current) {
      const observer = new ResizeObserver(() => {
        const height = nodeRef.current?.offsetHeight;
        data.onHeightChange?.(id, height ?? 0);
      });

      observer.observe(nodeRef.current);

      data.onHeightChange?.(id, nodeRef.current.offsetHeight);

      return () => observer.disconnect();
    }
  }, [id, data]);

  return (
    <StyledBox ref={nodeRef}>
      <Handle
        type="target"
        id={HandleId.TopCenter}
        position={Position.Top}
        isConnectable={isConnectable}
        className={classes.handle}
      />
      <Card
        variant="outlined"
        className={`${classes.card} ${selected ? classes.cardShadowSelected : ''}`}>
        <CardContent className={classes.cardContent}>
          <h5 className={classes.header}>{title}</h5>
        </CardContent>
      </Card>
      <Handle
        type="source"
        id={HandleId.BottomCenter}
        position={Position.Bottom}
        isConnectable={isConnectable}
        className={classes.handle}
      />
    </StyledBox>
  );
};

export default memo(WorkflowSmallStepNode);
